import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals } from '../globals';

export interface Quot {
  active: number;
  createdAt: any;
  updatedAt: any;

  folio: string;
  date: string;
  status: string;
  shortDescription: string;
  deliveryCommitment: string;
  longDescription: string;
  iva: number;
  utility: number;
  payConditions: string;
  notes: string;
  notesPrivate: string;
  
  quantities: number[];
  sections: any[];
  generalActivities: any[];
  finalTableHeaders: string[];
  finalTableRows: any[];
  
  customerRef: any;
  keyPersonName: string;
  keyPersonJob: string;
  employeeSignRef: any;
  createdByRef: any;
  prVntaRef: any;
}
interface QuotId extends Quot {
  id: string;
}

@Injectable()
export class QuotService {

  quotCollection: AngularFirestoreCollection<Quot>;
  quots: any;

  quotDocument: AngularFirestoreDocument<QuotId>;
  quot: Observable<QuotId>;

  collection: string;
  customerCollection: string;
  keyPersonCollection: string;
  employeeCollection: string;
  userCollection: string;
  prVntaCollection: string;
  companyCollection: string;
  companyId: string;

  // NOTE: Change this any for Quot Interface
  quot$: Observable<any>;
  quots$: Observable<any[]>;

  constructor(
    public afh: FirestoreService,
    private router: Router,
  ) {
    this.collection = Globals.QUOT_COLLECTION;
    this.customerCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.keyPersonCollection = Globals.KEY_PERSONS_COLLECTION;
    this.employeeCollection = Globals.EMPLOYEE_COLLECTION;
    this.userCollection = Globals.USER_COLLECTION;
    this.prVntaCollection = Globals.PRVNTA_COLLECTION;
    this.companyCollection = Globals.COMPANIES_COLLECTION;
    this.companyId = Globals.AGRO_VIVA_DOCUMENT_ID;
  }

  /**
   * Get all quots with references
   * @return {Observable} Arrays of quots
   */
  getQuots({onEmpty=undefined}={}) {
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('folio', 'desc').where('active', '==', true) ).map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        const customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
        const employeeSignRef = this.afh.docWithIds$(`${document.employeeSignRef.path}`);
        const createdByRef = this.afh.docWithIds$(`${document.createdByRef.path}`);
        const prVntaRef = this.afh.docWithIds$(`${document.prVntaRef.path}`);
        const companyRef = this.afh.docWithIds$(`${this.companyCollection}/${this.companyId}`);
        return Observable.combineLatest(
          customerRef,
          employeeSignRef,
          createdByRef,
          prVntaRef,
          companyRef,
          (customerData, employeeSignData, createdByData, prVntaData, companyData) => {
            return {
              customer: customerData,
              employeeSign: employeeSignData,
              createdBy: createdByData,
              prVnta: prVntaData,
              company: companyData
            };
        })
        .map(data => {
          return {
            customerObj: data.customer,
            employeeSignObj: data.employeeSign,
            createdByObj: data.createdBy,
            prVntaObj: data.prVnta,
            companyObj: data.company,
            ...document
          };
        });
      })
    }).mergeMap(quotsDocuments => Observable.combineLatest(quotsDocuments));
  }

  /**
   * Get all quots with references
   * @return {Observable} Arrays of quots
   */
  getQuotsFifteenDays({onEmpty=undefined}={}) {
    let filteredDate = new Date();
    filteredDate.setDate(filteredDate.getDate() - 15);
    let date = this.stringifyDate(filteredDate);
    return this.afh.colWithIds$(this.collection, ref => ref.where('date', '>', date).where('active', '==', true) ).map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        const customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
        const employeeSignRef = this.afh.docWithIds$(`${document.employeeSignRef.path}`);
        const createdByRef = this.afh.docWithIds$(`${document.createdByRef.path}`);
        const prVntaRef = this.afh.docWithIds$(`${document.prVntaRef.path}`);
        const companyRef = this.afh.docWithIds$(`${this.companyCollection}/${this.companyId}`);
        return Observable.combineLatest(
          customerRef,
          employeeSignRef,
          createdByRef,
          prVntaRef,
          companyRef,
          (customerData, employeeSignData, createdByData, prVntaData, companyData) => {
            return {
              customer: customerData,
              employeeSign: employeeSignData,
              createdBy: createdByData,
              prVnta: prVntaData,
              company: companyData
            };
        })
        .map(data => {
          return {
            customerObj: data.customer,
            employeeSignObj: data.employeeSign,
            createdByObj: data.createdBy,
            prVntaObj: data.prVnta,
            companyObj: data.company,
            ...document
          };
        });
      })
    }).mergeMap(quotsDocuments => Observable.combineLatest(quotsDocuments));
  }

  /**
  * Get quot data
  * @param {string} sid - The id of the quot
  * @return {Observable} The Quot Object
  */
  getQuot(sid: string) {
    return this.quot$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {

      const customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
      const employeeSignRef = this.afh.docWithIds$(`${document.employeeSignRef.path}`);
      const createdByRef = this.afh.docWithIds$(`${document.createdByRef.path}`);
      const prVntaRef = this.afh.docWithIds$(`${document.prVntaRef.path}`);
      const companyRef = this.afh.docWithIds$(`${this.companyCollection}/${this.companyId}`);

      return Observable.combineLatest(
        customerRef,
        employeeSignRef,
        createdByRef,
        prVntaRef,
        companyRef,
        (customerData, employeeSignData, createdByData, prVntaData, companyData) => {
          return {
            customer: customerData,
            employeeSign: employeeSignData,
            createdBy: createdByData,
            prVnta: prVntaData,
            company: companyData
          };
      })
      .map(data => {
        return {
          customerObj: data.customer,
          employeeSignObj: data.employeeSign,
          createdByObj: data.createdBy,
          prVntaObj: data.prVnta,
          companyObj: data.company,
          ...document
        };
      });
    }).mergeMap(quotsDocuments => Observable.combineLatest(quotsDocuments));
  }


  /**
  * Create new quot
  * @param {Quot} quot - The data of the quot
  */
  addQuot(quot: Quot) {
    quot.customerRef = this.afh.doc(`${this.customerCollection}/${quot.customerRef}`).ref;
    quot.employeeSignRef = this.afh.doc(`${this.employeeCollection}/${quot.employeeSignRef}`).ref;
    quot.createdByRef = this.afh.doc(`${this.userCollection}/${quot.createdByRef}`).ref;
    quot.prVntaRef = this.afh.doc(`${this.prVntaCollection}/${quot.prVntaRef}`).ref;
    return this.afh.add(`${this.collection}`, quot);
  }

  /**
   * Update current quot
   * @param {string} sid  - The id of the quot
   * @param {Quot} quot - The data of the quot
   */
  updateQuot(sid: string, quot: any) {
    quot.customerRef = this.afh.doc(`${this.customerCollection}/${quot.customerRef}`).ref;
    quot.employeeSignRef = this.afh.doc(`${this.employeeCollection}/${quot.employeeSignRef}`).ref;
    quot.prVntaRef = this.afh.doc(`${this.prVntaCollection}/${quot.prVntaRef}`).ref;
    this.afh.update(`${this.collection}/${sid}`, quot);
  }

  /**
   * Delete current quot
   * @param {string} sid - The id of the quot
   */
  deleteQuot(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  windowReplace(action: string, id?: string){
    switch(action){
      case 'add':
      case 'create':
        this.router.navigate([`/new_quot/${id}`]);
        break;
      case 'edit':
      case 'update':
        this.router.navigate([`/edit_quot/${id}`]);
        break;
      case 'view':
      case 'display':
        this.router.navigate([`/vw_quot/${id}`]);
        break;
      case 'read':
        this.router.navigate([`quot`]);
        break;
      case 'odp':
        this.router.navigate([`add_production_order/${id}`]);
      default:
        console.log('Not an action');
    }
  }


  get today(){
    return this.stringifyDate(new Date());
  }
  stringifyDate(date: Date){
    var dd:string = date.getDate() + '';
    var mm:string = date.getMonth()+ 1 + '';
    var yyyy = date.getFullYear();
    if(+dd<10) dd = '0'+dd;
    if(+mm<10) mm = '0'+mm;
    return yyyy + '-' + mm + '-' + dd;
  }
}

export class QuotFilter {
  allRows: any[];

  dateIndex: number;
  customerIndex: number;
  statusIndex: number;

  constructor(allRows: any[], {dateIndex=0,customerIndex=0, statusIndex=0}){
    this.allRows = allRows.sort(({name}, {folio}) => name < folio ? 1 : -1);
    this.dateIndex = dateIndex;
    this.customerIndex = customerIndex;
    this.statusIndex = statusIndex;

    let customers = new Set( allRows.map(row => row.customerObj.name) );
    let sortedCustomers = [...customers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.customerSelect = ['Todos', ...sortedCustomers].map(name => ({name}) );
  }

  get filteredData() {
    return this.allRows
      .filter(({date}) => {
        let rowDate = new Date(date);
        let filterDate = new Date();
        let {amount, unit} = this.dateSelect[this.dateIndex];
        switch(unit){
          case 'day': filterDate.setDate(filterDate.getDate() - amount); break;
          case 'month': filterDate.setMonth(filterDate.getMonth() - amount); filterDate.setDate(0); break;
          case 'year': filterDate = new Date(filterDate.getFullYear() - amount, 0); break;
          default: filterDate.setFullYear(2015); break;
        }
        if(rowDate >= filterDate) return 1;
      })
      .filter(({customerName: rowName}) => {
        let {name: filterName} = this.customerSelect[this.customerIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      })
      .filter(({status: rowName}) => {
        let {name: filterName} = this.statusSelect[this.statusIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      });
  }

  get json(): string {
    return JSON.stringify({
      dateIndex: this.dateIndex,
      customerIndex: this.customerIndex,
      statusIndex: this.statusIndex
    });
  }

  dateSelect = [
    { amount: 15, unit: 'day', name: '15 días' },
    { amount: 0, unit: 'month', name: 'Este mes' },
    { amount: 6, unit: 'month', name: '6 meses' },
    { amount: 0, unit: 'year', name: 'Este año' },
    { amount: 0, unit: 'forever', name: 'Siempre' },
  ];
  customerSelect = [];
  statusSelect = [
    { name: 'Todos' },
    { name: 'Pendiente' },
    { name: 'Aceptada' },
    { name: 'Rechazada' },
  ];
}
